import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, AlertCircle, ArrowRight, ArrowLeft, ChevronDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function ProgressBar({ current, total }) {
  return (
    <div className="flex items-center gap-1.5 mb-8">
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          className={`h-1 flex-1 rounded-full transition-all duration-500 ${
            i < current ? "bg-[#C9A84C]" : i === current ? "bg-[#C9A84C]/40" : "bg-white/8"
          }`}
        />
      ))}
    </div>
  );
}

function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <span className="text-red-400 text-xs mt-1 flex items-center gap-1">
      <AlertCircle size={11} />{msg}
    </span>
  );
}

function InputField({ label, required, error, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-zinc-400 text-xs font-bold uppercase tracking-widest">
        {label}{required && " *"}
      </span>
      {children}
      <FieldError msg={error} />
    </label>
  );
}

function VraagRender({ vraag, value, onChange, error }) {
  const base = "w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors";

  if (vraag.type === "meerkeuze") {
    return (
      <InputField label={vraag.vraag} required={vraag.verplicht} error={error}>
        <div className="grid grid-cols-2 gap-2">
          {(vraag.opties || []).map(optie => (
            <button
              key={optie}
              type="button"
              onClick={() => onChange(optie)}
              className={`py-3 rounded-xl border text-sm font-semibold transition-all ${
                value === optie
                  ? "bg-[#C9A84C]/15 border-[#C9A84C] text-[#C9A84C]"
                  : "border-white/8 bg-[#1a1a1a] text-zinc-400 hover:border-[#C9A84C]/30"
              }`}
            >
              {optie}
            </button>
          ))}
        </div>
        <FieldError msg={error} />
      </InputField>
    );
  }

  if (vraag.type === "tekst_lang") {
    return (
      <InputField label={vraag.vraag} required={vraag.verplicht} error={error}>
        <textarea
          value={value || ""}
          onChange={e => onChange(e.target.value)}
          placeholder="Typ hier..."
          rows={3}
          className={`${base} resize-none`}
        />
      </InputField>
    );
  }

  if (vraag.type === "datum") {
    return (
      <InputField label={vraag.vraag} required={vraag.verplicht} error={error}>
        <input
          type="date"
          value={value || ""}
          onChange={e => onChange(e.target.value)}
          className={base}
        />
      </InputField>
    );
  }

  if (vraag.type === "getal") {
    return (
      <InputField label={vraag.vraag} required={vraag.verplicht} error={error}>
        <input
          type="number"
          value={value || ""}
          onChange={e => onChange(e.target.value)}
          placeholder="0"
          className={base}
        />
      </InputField>
    );
  }

  if (vraag.type === "ja_nee") {
    return (
      <InputField label={vraag.vraag} required={vraag.verplicht} error={error}>
        <div className="flex gap-3">
          {["Ja", "Nee"].map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(opt)}
              className={`flex-1 py-3 rounded-xl border text-sm font-semibold transition-all ${
                value === opt
                  ? "bg-[#C9A84C]/15 border-[#C9A84C] text-[#C9A84C]"
                  : "border-white/8 bg-[#1a1a1a] text-zinc-400"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
        <FieldError msg={error} />
      </InputField>
    );
  }

  // default: tekst
  return (
    <InputField label={vraag.vraag} required={vraag.verplicht} error={error}>
      <input
        type="text"
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        placeholder="Typ hier..."
        className={base}
      />
    </InputField>
  );
}

const ITEMS_PER_STEP = 4;

export default function CompanyIntakePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(null);
  const [antwoorden, setAntwoorden] = useState({});
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const alleVragen = formData
    ? [...(formData.basis_vragen || []), ...(formData.extra_vragen || [])]
    : [];

  const steps = [];
  for (let i = 0; i < alleVragen.length; i += ITEMS_PER_STEP) {
    steps.push(alleVragen.slice(i, i + ITEMS_PER_STEP));
  }

  useEffect(() => {
    fetch(`${API}/api/public/company-intake/${token}`)
      .then(r => {
        if (r.status === 410) { setErr("Dit formulier is al ingevuld. Bedankt!"); setLoading(false); return null; }
        if (!r.ok) { setErr("Formulier niet gevonden of verlopen."); setLoading(false); return null; }
        return r.json();
      })
      .then(d => {
        if (d) setFormData(d);
        setLoading(false);
      })
      .catch(() => { setErr("Formulier laden mislukt."); setLoading(false); });
  }, [token]);

  const validateStep = () => {
    const stepVragen = steps[step] || [];
    const newErrors = {};
    stepVragen.forEach(v => {
      if (v.verplicht && !antwoorden[v.id]) {
        newErrors[v.id] = "Dit veld is verplicht";
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    setStep(s => s + 1);
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    setStep(s => s - 1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/public/company-intake/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(antwoorden),
      });
      if (res.ok) {
        setDone(true);
      } else {
        const d = await res.json();
        setErr(d.detail || "Er is een fout opgetreden.");
      }
    } catch {
      setErr("Verzenden mislukt. Probeer het opnieuw.");
    }
    setSubmitting(false);
  };

  const primaryColor = formData?.branding?.primary_color || "#C9A84C";

  if (loading) return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-[#C9A84C]/30 border-t-[#C9A84C] rounded-full animate-spin" />
    </div>
  );

  if (err) return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center p-6">
      <div className="text-center max-w-sm">
        <AlertCircle size={40} className="text-zinc-500 mx-auto mb-4" />
        <h2 className="text-white font-bold text-xl mb-2">{err}</h2>
      </div>
    </div>
  );

  if (done) return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center p-6">
      <div className="text-center max-w-sm animate-fadeIn">
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
          style={{ background: `${primaryColor}20`, border: `1px solid ${primaryColor}40` }}
        >
          <CheckCircle size={36} style={{ color: primaryColor }} />
        </div>
        <h2 className="text-white font-bold text-2xl mb-3">Intake ingediend!</h2>
        <p className="text-zinc-400 text-sm leading-relaxed">
          Bedankt! Je trainer heeft je antwoorden ontvangen en neemt contact op.
        </p>
      </div>
    </div>
  );

  const isLaatsteStap = step === steps.length - 1;

  return (
    <div className="min-h-screen bg-[#080808] pb-16">
      {/* Header */}
      <div className="border-b border-white/5 px-4 py-4 sticky top-0 bg-[#080808] z-10">
        <div className="max-w-xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {step > 0 && (
              <button
                onClick={handleBack}
                className="w-8 h-8 rounded-xl bg-[#141414] border border-white/6 flex items-center justify-center text-zinc-400 hover:text-white"
              >
                <ArrowLeft size={15} />
              </button>
            )}
            <div>
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: primaryColor }}>
                {formData?.company_naam || "Intakeformulier"}
              </p>
              <p className="text-white font-bold text-sm">
                Stap {step + 1} van {steps.length}
              </p>
            </div>
          </div>
          <span className="text-zinc-600 text-xs font-bold">{step + 1}/{steps.length}</span>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-8">
        <ProgressBar current={step + 1} total={steps.length} />

        {formData?.sporter_naam && step === 0 && (
          <div className="mb-6">
            <h2 className="text-white font-bold text-xl">
              Hoi {formData.sporter_naam}!
            </h2>
            <p className="text-zinc-500 text-sm mt-1">
              Vul dit formulier in zodat je trainer je training optimaal kan afstemmen.
            </p>
          </div>
        )}

        <div className="space-y-5">
          {(steps[step] || []).map(vraag => (
            <VraagRender
              key={vraag.id}
              vraag={vraag}
              value={antwoorden[vraag.id]}
              onChange={val => {
                setAntwoorden(a => ({ ...a, [vraag.id]: val }));
                setErrors(e => ({ ...e, [vraag.id]: undefined }));
              }}
              error={errors[vraag.id]}
            />
          ))}
        </div>

        <div className="mt-8">
          <button
            onClick={isLaatsteStap ? handleSubmit : handleNext}
            disabled={submitting}
            data-testid="intake-next-btn"
            className="w-full py-4 rounded-xl font-bold text-sm uppercase tracking-wide flex items-center justify-center gap-2 transition-all disabled:opacity-60"
            style={{ background: primaryColor, color: "#000" }}
          >
            {submitting
              ? "Versturen..."
              : isLaatsteStap
              ? "Intake indienen"
              : (<>Volgende <ArrowRight size={15} /></>)
            }
          </button>
        </div>
      </div>
    </div>
  );
}
